import React, { useState } from 'react';
import { BookOpen, Download, Copy, Eye, EyeOff, CheckCircle, XCircle, Star, Users, Target, Compass } from 'lucide-react';

export const BrandGuidelinesDocument: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'rules' | 'examples' | 'dos-donts' | 'assets' | 'scenarios'>('overview');
  const [showPreview, setShowPreview] = useState(false);

  const GuidelineSection: React.FC<{
    title: string;
    description: string;
    content: React.ReactNode;
    icon: React.ReactNode;
  }> = ({ title, description, content, icon }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="flex items-center gap-3 mb-4">
        {icon}
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
      </div>
      <p className="text-sm text-charcoal-600 mb-4">{description}</p>
      {content}
    </div>
  );

  const RuleExample: React.FC<{
    title: string;
    description: string;
    rule: string;
    example: React.ReactNode;
    rationale: string;
  }> = ({ title, description, rule, example, rationale }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Rule:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{rule}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Example:</p>
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
      
      <div>
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Rationale:</p>
        <p className="text-sm text-charcoal-700">{rationale}</p>
      </div>
    </div>
  );

  const DoDontExample: React.FC<{
    title: string;
    doExample: React.ReactNode;
    dontExample: React.ReactNode;
    explanation: string;
  }> = ({ title, doExample, dontExample, explanation }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <h3 className="text-lg font-bold text-charcoal-900 mb-4">{title}</h3>
      
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-sage-600" />
            <span className="text-sm font-semibold text-sage-600">DO</span>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4 h-24 flex items-center justify-center relative overflow-hidden">
            {doExample}
          </div>
        </div>
        
        <div>
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 text-[var(--mustard)]" />
            <span className="text-sm font-semibold text-[var(--mustard)]">DON'T</span>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
            {dontExample}
          </div>
        </div>
      </div>
      
      <p className="text-sm text-charcoal-700">{explanation}</p>
    </div>
  );

  const AssetItem: React.FC<{
    title: string;
    description: string;
    format: string;
    size: string;
    downloadUrl: string;
    preview: React.ReactNode;
  }> = ({ title, description, format, size, downloadUrl: _downloadUrl, preview }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
          <p className="text-sm text-charcoal-600">{description}</p>
          <p className="text-xs text-charcoal-500 mt-1">Format: {format} | Size: {size}</p>
        </div>
        <button 
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showPreview ? 'Hide' : 'Show'} Preview
        </button>
      </div>
      
      {showPreview && (
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden mb-4">
          {preview}
        </div>
      )}
      
      <div className="flex gap-2">
        <button className="flex items-center gap-1 px-4 py-2 bg-consulting-slate-blue-600 text-white text-sm font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
          <Download className="h-4 w-4" />
          Download
        </button>
        <button className="flex items-center gap-1 px-4 py-2 bg-[var(--hover-tint)] text-charcoal-700 text-sm font-semibold hover:bg-[var(--hover-tint)] transition-colors">
          <Copy className="h-4 w-4" />
          Copy Link
        </button>
      </div>
    </div>
  );

  const ScenarioExample: React.FC<{
    title: string;
    context: string;
    application: string;
    outcome: string;
    example: React.ReactNode;
  }> = ({ title, context, application, outcome, example }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <h3 className="text-lg font-bold text-charcoal-900 mb-4">{title}</h3>
      
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm font-semibold text-charcoal-600 mb-2">Context:</p>
          <p className="text-sm text-charcoal-700">{context}</p>
        </div>
        
        <div>
          <p className="text-sm font-semibold text-charcoal-600 mb-2">Application:</p>
          <p className="text-sm text-charcoal-700">{application}</p>
        </div>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Outcome:</p>
        <p className="text-sm text-charcoal-700">{outcome}</p>
      </div>
      
      <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
        {example}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <BookOpen className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Brand Guidelines Document</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          The comprehensive container for all Peak Priorities brand rules, examples, dos/don'ts, downloadable assets, 
          and usage scenarios. This document ensures consistent brand application across all touchpoints.
        </p>
      </div>

      {/* System Overview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Target className="h-6 w-6 text-consulting-slate-blue-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Brand Guidelines System</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Our brand guidelines document serves as the single source of truth for all Peak Priorities brand applications. 
          It contains comprehensive rules, examples, and scenarios that ensure consistent brand expression across all touchpoints.
        </p>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Comprehensive</h3>
            <p className="text-sm text-[var(--mustard)]">Complete coverage of all brand elements and applications</p>
          </div>
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Practical</h3>
            <p className="text-sm text-consulting-slate-blue-800">Real-world examples and actionable guidelines</p>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4">
            <h3 className="font-bold text-sage-900 mb-2">Accessible</h3>
            <p className="text-sm text-sage-800">Easy to understand and implement by all team members</p>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Living Document</h3>
            <p className="text-sm text-[var(--mustard)]">Regularly updated to reflect brand evolution and new applications</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 flex-wrap">
        <button
          onClick={() => setActiveTab('overview')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'overview'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <BookOpen className="h-5 w-5" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('rules')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'rules'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Target className="h-5 w-5" />
          Rules
        </button>
        <button
          onClick={() => setActiveTab('examples')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'examples'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Star className="h-5 w-5" />
          Examples
        </button>
        <button
          onClick={() => setActiveTab('dos-donts')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'dos-donts'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <CheckCircle className="h-5 w-5" />
          Dos & Don'ts
        </button>
        <button
          onClick={() => setActiveTab('assets')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'assets'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Download className="h-5 w-5" />
          Assets
        </button>
        <button
          onClick={() => setActiveTab('scenarios')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'scenarios'
              ? 'bg-consulting-slate-blue-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Users className="h-5 w-5" />
          Scenarios
        </button>
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand Guidelines Overview</h2>
            <p className="text-charcoal-700 mb-6">
              This comprehensive brand guidelines document contains everything needed to maintain consistent brand expression 
              across all Peak Priorities touchpoints. It serves as the definitive reference for brand application.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Document Structure</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Brand Foundation</h4>
                    <p className="text-sm text-[var(--mustard)]">Mission, values, positioning, and brand essence</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Visual Identity</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Logo system, color palette, typography, and imagery</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Voice & Tone</h4>
                    <p className="text-sm text-sage-800">Communication guidelines, messaging, and voice attributes</p>
                  </div>
                  
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Application Guidelines</h4>
                    <p className="text-sm text-[var(--mustard)]">Usage rules, examples, and implementation guidance</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Sections</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Logo System</h4>
                    <p className="text-sm text-[var(--mustard)]">Complete logo variations, usage rules, and clear space requirements</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Color Palette</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Brand colors, semantic colors, and accessibility guidelines</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Typography</h4>
                    <p className="text-sm text-sage-800">Font families, hierarchy, and usage guidelines</p>
                  </div>
                  
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Templates</h4>
                    <p className="text-sm text-[var(--mustard)]">Pre-built templates for consistent brand application</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Brand Mission & Values */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand Mission & Values</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Mission Statement</h3>
                <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-6">
                  <p className="text-[var(--mustard)] font-medium">
                    "We help leaders navigate complexity to reach clarity. Our systematic approach to prioritization 
                    enables organizations to achieve measurable outcomes through structured decision-making processes."
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Core Values</h3>
                <div className="space-y-3">
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-1">Clarity</h4>
                    <p className="text-sm text-consulting-slate-blue-800">We bring clarity to complex situations</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-1">Precision</h4>
                    <p className="text-sm text-sage-800">We provide precise, actionable guidance</p>
                  </div>
                  
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-1">Results</h4>
                    <p className="text-sm text-[var(--mustard)]">We focus on measurable outcomes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules */}
      {activeTab === 'rules' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand Rules</h2>
            <p className="text-charcoal-700 mb-6">
              Essential rules that govern all brand applications. These rules ensure consistency and maintain brand integrity across all touchpoints.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <RuleExample
                title="Logo Clear Space"
                description="Minimum clear space around the logo"
                rule="The clear space around the logo must be at least 1x the height of the 'P' in Peak Priorities"
                rationale="Ensures the logo has sufficient breathing room and maintains visual impact"
                example={
                  <div className="relative w-24 h-16 border-2 border-[var(--mustard)] flex items-center justify-center">
                    <div className="w-8 h-8 bg-[var(--mustard)] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <div className="absolute inset-0 border-2 border-dashed border-[var(--mustard)]"></div>
                  </div>
                }
              />

              <RuleExample
                title="Color Usage"
                description="Primary color application rules"
                rule="Primary brand colors should be used for main elements, secondary colors for accents, and neutral colors for supporting elements"
                rationale="Maintains visual hierarchy and brand recognition while ensuring accessibility"
                example={
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="w-8 h-8 bg-sage-600 rounded"></div>
                    <div className="w-8 h-8 bg-[var(--mustard)] rounded"></div>
                    <div className="w-8 h-8 bg-[var(--surface)] rounded"></div>
                  </div>
                }
              />

              <RuleExample
                title="Typography Hierarchy"
                description="Text hierarchy and sizing rules"
                rule="Use Inter for headings and body text, maintain consistent line heights (1.5 for body, 1.2 for headings), and ensure minimum 16px font size for body text"
                rationale="Ensures readability and maintains consistent visual rhythm across all materials"
                example={
                  <div className="space-y-2">
                    <h1 className="text-lg font-bold text-charcoal-900">Heading 1</h1>
                    <h2 className="text-base font-semibold text-charcoal-900">Heading 2</h2>
                    <p className="text-sm text-charcoal-700">Body text example</p>
                  </div>
                }
              />

              <RuleExample
                title="Image Usage"
                description="Photography and imagery guidelines"
                rule="Use high-quality, professional imagery that reflects clarity and precision. Avoid stock photos that feel generic or overly staged"
                rationale="Reinforces brand values and maintains professional credibility"
                example={
                  <div className="w-16 h-12 bg-gradient-to-br from-consulting-slate-blue-100 to-sage-100 rounded flex items-center justify-center">
                    <span className="text-consulting-slate-blue-600 text-xs">Image</span>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Examples */}
      {activeTab === 'examples' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand Examples</h2>
            <p className="text-charcoal-700 mb-6">
              Real-world examples of proper brand application across various touchpoints and contexts.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <GuidelineSection
                title="Website Header"
                description="Proper logo placement and navigation styling"
                icon={<Compass className="h-5 w-5 text-consulting-slate-blue-600" />}
                content={
                  <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                      <span className="text-sm font-semibold text-charcoal-900">Peak Priorities</span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-xs text-charcoal-600">About</span>
                      <span className="text-xs text-charcoal-600">Services</span>
                      <span className="text-xs text-charcoal-600">Contact</span>
                    </div>
                  </div>
                }
              />

              <GuidelineSection
                title="Business Card"
                description="Professional business card layout and information hierarchy"
                icon={<Users className="h-5 w-5 text-consulting-slate-blue-600" />}
                content={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4 h-24 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-charcoal-900">John Smith</h4>
                      <p className="text-xs text-charcoal-600">Senior Consultant</p>
                      <p className="text-xs text-consulting-slate-blue-600">john@peakpriorities.com</p>
                    </div>
                    <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                  </div>
                }
              />

              <GuidelineSection
                title="Email Signature"
                description="Professional email signature with proper branding"
                icon={<Target className="h-5 w-5 text-consulting-slate-blue-600" />}
                content={
                  <div className="bg-[var(--surface)] p-4 h-24 flex items-center gap-3">
                    <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-charcoal-900">Sarah Johnson</h4>
                      <p className="text-xs text-charcoal-600">Peak Priorities | Senior Consultant</p>
                      <p className="text-xs text-consulting-slate-blue-600">sarah@peakpriorities.com</p>
                    </div>
                  </div>
                }
              />

              <GuidelineSection
                title="Presentation Slide"
                description="Consistent slide layout with proper branding"
                icon={<Star className="h-5 w-5 text-consulting-slate-blue-600" />}
                content={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4 h-24">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-charcoal-900">Strategic Priorities</h3>
                      <div className="w-6 h-6 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-2 bg-consulting-slate-blue-600 rounded w-3/4"></div>
                      <div className="h-2 bg-sage-600 rounded w-1/2"></div>
                      <div className="h-2 bg-[var(--mustard)] rounded w-2/3"></div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Dos & Don'ts */}
      {activeTab === 'dos-donts' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Dos & Don'ts</h2>
            <p className="text-charcoal-700 mb-6">
              Clear examples of correct and incorrect brand application to guide proper usage.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <DoDontExample
                title="Logo Usage"
                doExample={
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <span className="text-xs font-semibold text-charcoal-900">Peak Priorities</span>
                  </div>
                }
                dontExample={
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[var(--coral)] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <span className="text-xs font-semibold text-charcoal-900">Peak Priorities</span>
                  </div>
                }
                explanation="Use brand colors for the logo. Never change the logo colors to non-brand colors."
              />

              <DoDontExample
                title="Typography"
                doExample={
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-charcoal-900">Clear Heading</h3>
                    <p className="text-xs text-charcoal-700">Readable body text with proper contrast</p>
                  </div>
                }
                dontExample={
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-[var(--text-ter)]">Faded Heading</h3>
                    <p className="text-xs text-[var(--text-ter)]">Low contrast text that's hard to read</p>
                  </div>
                }
                explanation="Maintain proper contrast ratios and use brand typography. Avoid low contrast text that reduces readability."
              />

              <DoDontExample
                title="Color Application"
                doExample={
                  <div className="flex gap-2">
                    <div className="w-6 h-6 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="w-6 h-6 bg-sage-600 rounded"></div>
                    <div className="w-6 h-6 bg-[var(--mustard)] rounded"></div>
                  </div>
                }
                dontExample={
                  <div className="flex gap-2">
                    <div className="w-6 h-6 bg-[var(--coral)] rounded"></div>
                    <div className="w-6 h-6 bg-[var(--slate)] rounded"></div>
                    <div className="w-6 h-6 bg-[var(--green)] rounded"></div>
                  </div>
                }
                explanation="Use brand colors consistently. Avoid introducing non-brand colors that dilute brand recognition."
              />

              <DoDontExample
                title="Spacing"
                doExample={
                  <div className="space-y-2">
                    <div className="h-3 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="h-3 bg-sage-600 rounded"></div>
                    <div className="h-3 bg-[var(--mustard)] rounded"></div>
                  </div>
                }
                dontExample={
                  <div className="space-y-0">
                    <div className="h-3 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="h-3 bg-sage-600 rounded"></div>
                    <div className="h-3 bg-[var(--mustard)] rounded"></div>
                  </div>
                }
                explanation="Maintain consistent spacing between elements. Avoid cramped layouts that reduce readability and visual appeal."
              />
            </div>
          </div>
        </div>
      )}

      {/* Assets */}
      {activeTab === 'assets' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Downloadable Assets</h2>
            <p className="text-charcoal-700 mb-6">
              All brand assets available for download, including logos, templates, and design elements.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AssetItem
                title="Logo Package"
                description="Complete logo system in all formats and variations"
                format="SVG, PNG, EPS, AI"
                size="Various sizes"
                downloadUrl="/assets/logo-package.zip"
                preview={
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <div className="w-8 h-8 bg-sage-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <div className="w-8 h-8 bg-[var(--mustard)] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                  </div>
                }
              />

              <AssetItem
                title="Color Palette"
                description="Brand colors in all formats for design applications"
                format="ASE, AI, PNG, CSS"
                size="Various formats"
                downloadUrl="/assets/color-palette.zip"
                preview={
                  <div className="flex gap-1">
                    <div className="w-6 h-6 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="w-6 h-6 bg-sage-600 rounded"></div>
                    <div className="w-6 h-6 bg-[var(--mustard)] rounded"></div>
                    <div className="w-6 h-6 bg-coral-600 rounded"></div>
                    <div className="w-6 h-6 bg-[var(--mustard)] rounded"></div>
                  </div>
                }
              />

              <AssetItem
                title="Typography Kit"
                description="Font files and typography guidelines"
                format="TTF, OTF, WOFF"
                size="Various weights"
                downloadUrl="/assets/typography-kit.zip"
                preview={
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-charcoal-900">Inter Bold</h3>
                    <h3 className="text-sm font-semibold text-charcoal-900">Inter Semibold</h3>
                    <h3 className="text-sm font-medium text-charcoal-900">Inter Medium</h3>
                    <h3 className="text-sm text-charcoal-900">Inter Regular</h3>
                  </div>
                }
              />

              <AssetItem
                title="Template Library"
                description="Complete set of brand templates for all applications"
                format="DOCX, PPTX, PSD, AI"
                size="Various sizes"
                downloadUrl="/assets/template-library.zip"
                preview={
                  <div className="grid grid-cols-2 gap-1">
                    <div className="w-6 h-4 bg-consulting-slate-blue-600 rounded"></div>
                    <div className="w-6 h-4 bg-sage-600 rounded"></div>
                    <div className="w-6 h-4 bg-[var(--mustard)] rounded"></div>
                    <div className="w-6 h-4 bg-coral-600 rounded"></div>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Scenarios */}
      {activeTab === 'scenarios' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Usage Scenarios</h2>
            <p className="text-charcoal-700 mb-6">
              Real-world scenarios demonstrating proper brand application in various contexts and situations.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <ScenarioExample
                title="Client Presentation"
                context="Presenting to a Fortune 500 executive team about prioritization strategy"
                application="Use executive presentation template with primary brand colors, professional imagery, and clear data visualizations"
                outcome="Reinforces credibility and professionalism while maintaining brand consistency"
                example={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-3 h-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-bold text-charcoal-900">Strategic Priorities</h3>
                      <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1 bg-consulting-slate-blue-600 rounded w-3/4"></div>
                      <div className="h-1 bg-sage-600 rounded w-1/2"></div>
                      <div className="h-1 bg-[var(--mustard)] rounded w-2/3"></div>
                    </div>
                  </div>
                }
              />

              <ScenarioExample
                title="Social Media Campaign"
                context="Launching a thought leadership campaign on LinkedIn"
                application="Use branded social media templates with consistent messaging, professional imagery, and clear call-to-actions"
                outcome="Builds brand recognition and establishes thought leadership in the prioritization space"
                example={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-3 h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                      <span className="text-xs font-semibold text-charcoal-900">Peak Priorities</span>
                    </div>
                    <p className="text-xs text-charcoal-700">Transform your prioritization process with our systematic approach...</p>
                  </div>
                }
              />

              <ScenarioExample
                title="Workshop Materials"
                context="Conducting a prioritization workshop for a client team"
                application="Use interactive worksheets with clear instructions, branded templates, and consistent visual hierarchy"
                outcome="Enhances learning experience and reinforces brand values through consistent application"
                example={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-3 h-full">
                    <h3 className="text-xs font-bold text-charcoal-900 mb-1">Priority Assessment</h3>
                    <div className="space-y-1">
                      <div className="h-1 bg-consulting-slate-blue-600 rounded w-full"></div>
                      <div className="h-1 bg-sage-600 rounded w-3/4"></div>
                      <div className="h-1 bg-[var(--mustard)] rounded w-1/2"></div>
                    </div>
                  </div>
                }
              />

              <ScenarioExample
                title="Email Communication"
                context="Sending a monthly update to clients about project progress"
                application="Use branded email template with consistent typography, professional signature, and clear information hierarchy"
                outcome="Maintains professional communication standards while reinforcing brand identity"
                example={
                  <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-3 h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-consulting-slate-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                      <span className="text-xs font-semibold text-charcoal-900">Monthly Update</span>
                    </div>
                    <p className="text-xs text-charcoal-700">Project progress and next steps...</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Implementation Guidelines */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-charcoal-700 max-w-3xl mx-auto mb-6">
          This brand guidelines document serves as the definitive reference for all Peak Priorities brand applications. 
          Use it consistently to maintain brand integrity and ensure professional presentation across all touchpoints.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-consulting-slate-blue-600 text-white font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
            <Download className="h-5 w-5" />
            Download Full Guidelines
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--bg-raised)] text-charcoal-900 border-2 border-consulting-slate-blue-600 font-semibold hover:bg-consulting-slate-blue-50 transition-colors">
            <Copy className="h-5 w-5" />
            Request Brand Review
          </button>
        </div>
      </div>
    </div>
  );
};




